import axios from 'axios';
import apiService from '../../../services/apiService';

const COMMON_API = '/ykrestapi/CommonChannel';
const API_BASE_URL = process.env.REACT_APP_YK_API_BASE || 'http://localhost:5000/ykrestapi';

const APP_NAME = 'Donation';
const APP_ENTITY = 'Temple';

const isCancel = (error) =>
  axios.isCancel?.(error) ||
  error?.code === 'ERR_CANCELED' ||
  error?.name === 'CanceledError' ||
  error?.name === 'AbortError';

const groceryService = {
  isCancel,

  getGroceryItems: async ({ signal } = {}) => {
    try {
      const response = await apiService.get(`${COMMON_API}/GetGroceryItems`, { signal });
      const items = Array.isArray(response.data) ? response.data : [];

      return items
        .filter((item) => item?.isActive === true || item?.isActive === 1)
        .sort((a, b) => (a?.displayOrder ?? 0) - (b?.displayOrder ?? 0))
        .map((item) => ({
          itemId: item.id ?? item.itemId,
          itemName: item.name ?? item.itemName ?? '',
          estimatedPrice: Number(item.estimatedPrice ?? 0),
          icon: item.icon ?? '',
          itemImage_URL: item.itemImage_URL ?? null,
          unit: item.unit ?? '',
          displayOrder: item.displayOrder ?? 0,
        }));
    } catch (error) {
      if (isCancel(error)) throw error;
      console.error('Error fetching grocery items:', error?.response?.data || error);
      throw error;
    }
  },

  addGroceryDonation: async (donationData, { signal } = {}) => {
    try {
      const response = await apiService.post(`${COMMON_API}/AddGroceryDonation`, donationData, { signal });
      return response.data;
    } catch (error) {
      if (isCancel(error)) throw error;
      console.error('Error saving grocery donation:', error?.response?.data || error);
      throw error;
    }
  },

  sendGroceryDonationEmail: async (donationData) => {
    try {
      const response = await apiService.post(`${COMMON_API}/SendGroceryDonationEmail`, donationData);
      return response.data;
    } catch (error) {
      console.warn('Grocery confirmation email failed (non-blocking):', error?.response?.data || error?.message);
      return { success: false };
    }
  },

  // Backend signature is VerifyCaptcha([FromBody] string captchaToken), so the
  // token goes up as a bare JSON string literal rather than an object.
  verifyRecaptcha: async (token) => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/PaymentChannel/verify-recaptcha`,
        JSON.stringify(token),
        {
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          timeout: 15000,
        }
      );
      return response.data || { success: false };
    } catch (error) {
      console.error('reCAPTCHA verification failed:', error?.response?.data || error?.message);
      return { success: false };
    }
  },

  processOneTimePayment: async (payload) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/PaymentChannel/AuthNetOneTimePayment`, payload, {
        headers: { 'Content-Type': 'application/json' },
      });
      return response.data;
    } catch (error) {
      console.error('One-time payment error:', error?.response?.data || error);
      throw error;
    }
  },

  processRecurringPayment: async (payload, studentId = '0') => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/PaymentChannel/AuthNetRecurringPayment?studentId=${encodeURIComponent(studentId)}`,
        payload,
        { headers: { 'Content-Type': 'application/json' } }
      );
      return response.data;
    } catch (error) {
      console.error('Recurring payment error:', error?.response?.data || error);
      throw error;
    }
  },

  buildDonationPayload: ({ donor, selectedItems, totalAmount, isPhysical, paymentMethod, isRecurring, dropOff, transactionId, subscriptionId }) => {
    const itemSummary = isPhysical
      ? selectedItems.map((item) => item.itemName).join(', ')
      : selectedItems.map((item) => `${item.itemName} (${item.quantity})`).join(', ');

    const payload = {
      app: APP_NAME,
      entity: APP_ENTITY,
      firstName: donor.firstName,
      lastName: donor.lastName,
      email: donor.email,
      phoneNo: donor.phone,
      address: donor.address,
      country: donor.country,
      state: donor.state,
      city: donor.city,
      postalCode: donor.postalCode,
      purposeOfDonation: isPhysical
        ? 'Grocery Donation - Physical Drop-Off'
        : `Grocery Donation - Online Payment${isRecurring ? ' (Recurring)' : ''}`,
      paymentFor: itemSummary,
      donationAmount: totalAmount,
      donationType: 'Grocery',
      paymentType: isPhysical
        ? 'Physical Drop-Off'
        : `Online Payment - ${paymentMethod === 'creditcard' ? 'Credit Card' : 'ACH'}`,
      groceryItems: selectedItems.map((item) => ({
        ItemId: item.itemId,
        ItemName: item.itemName,
        Quantity: item.quantity,
        Unit: item.unit,
        EstimatedPrice: item.estimatedPrice,
      })),
      specialInstructions: donor.specialInstructions || '',
    };

    // GroceryDonationDTO carries no drop-off/transaction fields, so keep those
    // details with the record by appending them to SpecialInstructions.
    const notes = [donor.specialInstructions || ''];

    if (isPhysical) {
      if (dropOff?.date) {
        notes.push(`Drop-off: ${dropOff.date}${dropOff.time ? ` (${dropOff.time})` : ''}`);
      }
    } else {
      if (transactionId) notes.push(`Transaction ID: ${transactionId}`);
      if (subscriptionId) notes.push(`Subscription ID: ${subscriptionId}`);
    }

    payload.specialInstructions = notes.filter(Boolean).join(' | ');

    return payload;
  },

  buildAuthNetPayload: ({ donor, card, ach, selectedItems, totalAmount, paymentMethod, isRecurring, recurring }) => {
    const description = `Grocery Donation - ${selectedItems
      .map((item) => `${item.itemName} (${item.quantity})`)
      .join(', ')}`;

    const isCard = paymentMethod === 'creditcard';
    const billingZip = card?.billingZipCode || '';

    const address = {
      streetAddress1: '',
      streetAddress2: '-',
      city: '',
      stateCode: '',
      zipCode: billingZip,
      country: 'US',
    };

    return {
      app: APP_NAME,
      entity: APP_ENTITY,
      paymentType: isCard ? 'cc' : 'ach',
      customerEmail: donor.email,
      orderid: '0',
      amount: totalAmount.toString(),
      description,
      customer: {
        firstName: donor.firstName,
        lastName: donor.lastName,
        email: donor.email,
        phone: (donor.phone || '').replace(/\D/g, ''),
        company: 'string',
        notes: description,
        customerAccount: 'string',
        shippingSameAsBilling: true,
        billingAddress: { ...address },
        shippingAddress: { ...address },
      },
      creditcard: isCard
        ? {
            creditCardNumber: (card?.cardNumber || '').replace(/\s/g, ''),
            expirationDate: card?.expiryDate,
            issuer: 'string',
            billingZipCode: billingZip,
            customerId: 'string',
            cvv: card?.cvv,
          }
        : {
            creditCardNumber: 'string',
            expirationDate: 'string',
            issuer: 'string',
            billingZipCode: 'string',
            customerId: 'string',
            cvv: 'string',
          },
      ach: !isCard
        ? {
            isCheckingAccount: ach?.accountType === 'Checking' ? 'Y' : 'N',
            routingNumber: ach?.routingNumber,
            accountNumber: ach?.accountNumber,
            bankName: ach?.bankName,
            customerId: 'string',
            isDefault: 'string',
          }
        : {
            isCheckingAccount: 'string',
            routingNumber: 'string',
            accountNumber: 'string',
            bankName: 'string',
            customerId: 'string',
            isDefault: 'string',
          },
      recurr: isRecurring
        ? groceryService.buildRecurringBlock({ totalAmount, description, recurring })
        : {
            paymentAmount: 'string',
            accountId: 'string',
            startDate: 'string',
            executionFrequencyType: 'string',
            executionFrequencyParameter: 'string',
            endDate: 'string',
            paymentSubType: 'string',
            invoiceNumber: 'string',
            firstPaymentAmount: 'string',
            firstPaymentDate: 'string',
            description: 'string',
          },
    };
  },

  buildRecurringBlock: ({ totalAmount, description, recurring }) => {
    const startSource = recurring?.startDate || new Date().toISOString().split('T')[0];
    const startDate = new Date(`${startSource}T12:00:00`);

    let endDate;
    if (recurring?.endType === 'NoEndDate') {
      // Authorize.Net caps total occurrences; push the end far out rather than omitting it.
      const farFuture = new Date(startDate);
      farFuture.setFullYear(farFuture.getFullYear() + 833);
      farFuture.setMonth(farFuture.getMonth() + 2);
      endDate = farFuture.toISOString();
    } else if (recurring?.endType === 'EndsAfterTerms') {
      const terms = parseInt(recurring?.numberOfTerms || '12', 10);
      const computed = new Date(startDate);
      computed.setMonth(computed.getMonth() + terms);
      endDate = computed.toISOString();
    } else {
      endDate = new Date(`${recurring?.endDate || startSource}T12:00:00`).toISOString();
    }

    return {
      paymentAmount: totalAmount.toString(),
      accountId: 'string',
      startDate: startDate.toISOString(),
      executionFrequencyType: 'Monthly',
      executionFrequencyParameter: '1',
      endDate,
      paymentSubType: 'string',
      invoiceNumber: 'string',
      firstPaymentAmount: totalAmount.toString(),
      firstPaymentDate: new Date().toISOString().split('T')[0],
      description,
    };
  },

  extractPaymentResult: (paymentResponse) => {
    const messages =
      paymentResponse?.messages ||
      paymentResponse?.createTransactionResponse?.messages ||
      paymentResponse?.aRBCreateSubscriptionResponse?.messages;

    const resultCode = messages?.resultCode || messages?.ResultCode || '';
    const approved = String(resultCode).toLowerCase() === 'ok';

    const errorText =
      paymentResponse?.createTransactionResponse?.transactionResponse?.errors?.[0]?.errorText ||
      messages?.message?.[0]?.text ||
      paymentResponse?.errorMessage ||
      '';

    return {
      approved,
      errorText,
      transactionId:
        paymentResponse?.transactionId ||
        paymentResponse?.transId ||
        paymentResponse?.createTransactionResponse?.transactionResponse?.transId ||
        '',
      subscriptionId:
        paymentResponse?.aRBCreateSubscriptionResponse?.subscriptionId ||
        paymentResponse?.subscriptionId ||
        '',
    };
  },
};

export default groceryService;
